import api from '../services/api';

/**
 * Servicio de Facturación Electrónica
 * 
 * Este servicio maneja todas las operaciones relacionadas con la facturación electrónica,
 * incluyendo la gestión de clientes, facturas, configuración y catálogos SAT.
 * 
 * Todos los endpoints están preparados para conectarse con una API real del backend.
 */

export class FacturacionService {
    
    // ==================== CLIENTES DE FACTURACIÓN ====================
    
    /**
     * Obtiene todos los clientes de facturación con paginación y filtros
     * @param {Object} params - Parámetros de consulta
     * @param {string} params.search - Término de búsqueda
     * @param {number} params.page - Página actual
     * @param {number} params.pageSize - Tamaño de página
     * @returns {Promise} Respuesta de la API con lista de clientes
     */
    static async getClientes(params = {}) {
        const queryParams = new URLSearchParams();
        
        if (params.search) queryParams.append('search', params.search);
        if (params.page) queryParams.append('page', params.page);
        if (params.pageSize) queryParams.append('pageSize', params.pageSize);
        if (params.activo !== undefined) queryParams.append('activo', params.activo);
        
        return api.get(`/Facturacion/clientes?${queryParams.toString()}`);
    }

    /**
     * Obtiene un cliente de facturación por ID
     * @param {number} id - ID del cliente
     * @returns {Promise} Respuesta de la API con datos del cliente
     */
    static async getClienteById(id) {
        return api.get(`/Facturacion/clientes/${id}`);
    }

    /**
     * Crea un nuevo cliente de facturación
     * @param {Object} clientData - Datos del cliente
     * @returns {Promise} Respuesta de la API con el cliente creado
     */
    static async createCliente(clientData) {
        return api.post('/Facturacion/clientes', {
            rfc: clientData.rfc,
            razonSocial: clientData.razonSocial,
            regimenFiscal: clientData.regimenFiscal,
            usoCFDI: clientData.usoCFDI,
            direccion: clientData.direccion,
            contacto: clientData.contacto
        });
    }

    /**
     * Actualiza un cliente de facturación existente
     * @param {number} id - ID del cliente
     * @param {Object} clientData - Datos actualizados del cliente
     * @returns {Promise} Respuesta de la API con el cliente actualizado
     */
    static async updateCliente(id, clientData) {
        return api.put(`/Facturacion/clientes/${id}`, clientData);
    }

    /**
     * Elimina un cliente de facturación
     * @param {number} id - ID del cliente
     * @returns {Promise} Respuesta de la API
     */
    static async deleteCliente(id) {
        return api.delete(`/Facturacion/clientes/${id}`);
    }

    /**
     * Busca clientes por término de búsqueda
     * @param {string} query - Término de búsqueda
     * @returns {Promise} Respuesta de la API con resultados de búsqueda
     */
    static async searchClientes(query) {
        return api.get(`/Facturacion/clientes/search?q=${encodeURIComponent(query)}`);
    }

    // ==================== FACTURAS ====================

    /**
     * Crea una nueva factura
     * @param {Object} facturaData - Datos de la factura
     * @returns {Promise} Respuesta de la API con la factura creada
     */
    static async createFactura(facturaData) {
        return api.post('/Facturacion/facturas', {
            clienteId: facturaData.clientId,
            conceptos: facturaData.items.map(item => ({
                cantidad: item.quantity,
                descripcion: item.description,
                valorUnitario: item.unitPrice,
                importe: item.total
            })),
            metodoPago: facturaData.metodoPago || 'PUE',
            formaPago: facturaData.formaPago || '03',
            moneda: facturaData.moneda || 'MXN'
        });
    }

    /**
     * Obtiene todas las facturas con filtros
     * @param {Object} params - Parámetros de consulta
     * @returns {Promise} Respuesta de la API con lista de facturas
     */
    static async getFacturas(params = {}) {
        const queryParams = new URLSearchParams();
        
        if (params.clientId) queryParams.append('clientId', params.clientId);
        if (params.status) queryParams.append('status', params.status);
        if (params.fechaDesde) queryParams.append('fechaDesde', params.fechaDesde);
        if (params.fechaHasta) queryParams.append('fechaHasta', params.fechaHasta);
        if (params.page) queryParams.append('page', params.page);
        if (params.pageSize) queryParams.append('pageSize', params.pageSize);
        
        return api.get(`/Facturacion/facturas?${queryParams.toString()}`);
    }

    /**
     * Obtiene una factura por ID
     * @param {number} id - ID de la factura
     * @returns {Promise} Respuesta de la API con datos de la factura
     */
    static async getFacturaById(id) {
        return api.get(`/Facturacion/facturas/${id}`);
    }

    /**
     * Descarga el PDF de una factura
     * @param {number} id - ID de la factura
     * @returns {Promise} Respuesta de la API con el archivo PDF
     */
    static async downloadFacturaPDF(id) {
        return api.get(`/Facturacion/facturas/${id}/pdf`, { 
            responseType: 'blob',
            headers: {
                'Accept': 'application/pdf'
            }
        });
    }

    /**
     * Descarga el XML de una factura
     * @param {number} id - ID de la factura
     * @returns {Promise} Respuesta de la API con el archivo XML
     */
    static async downloadFacturaXML(id) {
        return api.get(`/Facturacion/facturas/${id}/xml`, { 
            responseType: 'blob',
            headers: {
                'Accept': 'application/xml'
            }
        });
    }

    /**
     * Timbra una factura ante el SAT
     * @param {number} id - ID de la factura
     * @returns {Promise} Respuesta de la API con el resultado del timbrado
     */
    static async timbrarFactura(id) {
        return api.post(`/Facturacion/facturas/${id}/timbrar`);
    }

    /**
     * Cancela una factura ante el SAT
     * @param {number} id - ID de la factura
     * @param {string} motivo - Motivo de cancelación
     * @returns {Promise} Respuesta de la API con el resultado de la cancelación
     */
    static async cancelarFactura(id, motivo) {
        return api.post(`/Facturacion/facturas/${id}/cancelar`, { 
            motivo: motivo 
        });
    }

    // ==================== CONFIGURACIÓN ====================

    /**
     * Obtiene la configuración de facturación
     * @returns {Promise} Respuesta de la API con la configuración
     */
    static async getConfiguracion() {
        return api.get('/Facturacion/configuracion');
    }

    /**
     * Actualiza la configuración de facturación
     * @param {Object} config - Configuración actualizada
     * @returns {Promise} Respuesta de la API
     */
    static async updateConfiguracion(config) {
        return api.put('/Facturacion/configuracion', config);
    }

    /**
     * Prueba la validez de los certificados de sello digital
     * @param {Object} certificados - Datos de los certificados
     * @param {string} certificados.certificado - Certificado en base64
     * @param {string} certificados.llavePrivada - Llave privada en base64
     * @param {string} certificados.password - Contraseña de la llave privada
     * @returns {Promise} Respuesta de la API con el resultado de la validación
     */
    static async testCertificados(certificados) {
        return api.post('/Facturacion/configuracion/test-certificados', {
            certificado: certificados.certificado,
            llavePrivada: certificados.llavePrivada,
            password: certificados.password
        });
    }

    /**
     * Prueba la conexión con el PAC (Proveedor Autorizado de Certificación)
     * @param {Object} pacConfig - Configuración del PAC
     * @param {string} pacConfig.pac - Nombre del PAC
     * @param {string} pacConfig.usuario - Usuario del PAC
     * @param {string} pacConfig.password - Contraseña del PAC
     * @param {string} pacConfig.urlTimbrado - URL de timbrado
     * @param {string} pacConfig.urlCancelacion - URL de cancelación
     * @returns {Promise} Respuesta de la API con el resultado de la prueba
     */
    static async testPAC(pacConfig) {
        return api.post('/Facturacion/configuracion/test-pac', {
            pac: pacConfig.pac,
            usuario: pacConfig.usuario,
            password: pacConfig.password,
            urlTimbrado: pacConfig.urlTimbrado,
            urlCancelacion: pacConfig.urlCancelacion
        });
    }

    // ==================== CATÁLOGOS SAT ====================

    /**
     * Obtiene el catálogo de regímenes fiscales del SAT
     * @returns {Promise} Respuesta de la API con el catálogo
     */
    static async getRegimenFiscal() {
        return api.get('/Facturacion/catalogos/regimen-fiscal');
    }

    /**
     * Obtiene el catálogo de usos de CFDI del SAT
     * @returns {Promise} Respuesta de la API con el catálogo
     */
    static async getUsoCFDI() {
        return api.get('/Facturacion/catalogos/uso-cfdi');
    }

    /**
     * Obtiene el catálogo de métodos de pago del SAT
     * @returns {Promise} Respuesta de la API con el catálogo
     */
    static async getMetodosPago() {
        return api.get('/Facturacion/catalogos/metodos-pago');
    }

    /**
     * Obtiene el catálogo de formas de pago del SAT
     * @returns {Promise} Respuesta de la API con el catálogo
     */
    static async getFormasPago() {
        return api.get('/Facturacion/catalogos/formas-pago');
    }

    /**
     * Obtiene el catálogo de monedas del SAT
     * @returns {Promise} Respuesta de la API con el catálogo
     */
    static async getMonedas() {
        return api.get('/Facturacion/catalogos/monedas');
    }

    /**
     * Obtiene el catálogo de unidades de medida del SAT
     * @returns {Promise} Respuesta de la API con el catálogo
     */
    static async getUnidades() {
        return api.get('/Facturacion/catalogos/unidades');
    }

    // ==================== UTILIDADES ====================

    /**
     * Valida un RFC mexicano
     * @param {string} rfc - RFC a validar
     * @returns {boolean} True si el RFC es válido
     */
    static validateRFC(rfc) {
        const rfcPattern = /^[A-ZÑ&]{3,4}[0-9]{6}[A-Z0-9]{3}$/;
        return rfcPattern.test(rfc);
    }

    /**
     * Formatea un monto como moneda mexicana
     * @param {number} amount - Monto a formatear
     * @returns {string} Monto formateado
     */
    static formatCurrency(amount) {
        return new Intl.NumberFormat('es-MX', {
            style: 'currency',
            currency: 'MXN'
        }).format(amount);
    }

    /**
     * Calcula el IVA de un monto
     * @param {number} subtotal - Subtotal
     * @param {number} rate - Tasa de IVA (por defecto 0.16)
     * @returns {number} Monto del IVA
     */
    static calculateIVA(subtotal, rate = 0.16) {
        return subtotal * rate;
    }

    /**
     * Genera un folio único para factura
     * @param {string} serie - Serie de la factura
     * @param {number} numero - Número consecutivo
     * @returns {string} Folio completo
     */
    static generateFolio(serie, numero) {
        return `${serie}-${numero.toString().padStart(6, '0')}`;
    }
}

// Exportar también como objeto para compatibilidad
export const facturacionService = {
    clientes: {
        getAll: FacturacionService.getClientes,
        getById: FacturacionService.getClienteById,
        create: FacturacionService.createCliente,
        update: FacturacionService.updateCliente,
        delete: FacturacionService.deleteCliente,
        search: FacturacionService.searchClientes
    },
    facturas: {
        create: FacturacionService.createFactura,
        getAll: FacturacionService.getFacturas,
        getById: FacturacionService.getFacturaById,
        getPDF: FacturacionService.downloadFacturaPDF,
        getXML: FacturacionService.downloadFacturaXML,
        timbrar: FacturacionService.timbrarFactura,
        cancel: FacturacionService.cancelarFactura
    },
    configuracion: {
        get: FacturacionService.getConfiguracion,
        update: FacturacionService.updateConfiguracion,
        testCertificados: FacturacionService.testCertificados,
        testPAC: FacturacionService.testPAC
    },
    catalogos: {
        getRegimenFiscal: FacturacionService.getRegimenFiscal,
        getUsoCFDI: FacturacionService.getUsoCFDI,
        getMetodosPago: FacturacionService.getMetodosPago,
        getFormasPago: FacturacionService.getFormasPago,
        getMonedas: FacturacionService.getMonedas,
        getUnidades: FacturacionService.getUnidades
    },
    utils: {
        validateRFC: FacturacionService.validateRFC,
        formatCurrency: FacturacionService.formatCurrency,
        calculateIVA: FacturacionService.calculateIVA,
        generateFolio: FacturacionService.generateFolio
    }
};

export default FacturacionService;